import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Dialog,
  Button,
  Dropdown,
  TextInput,
  DropdownWithSearch,
  Image,
} from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
} from "./AddEditMentionGroup.styled";
import "@utils/extra-style.scss";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditMentionGroup = (props) => {
  const {
    t,
    id,
    isEditMode,
    title,
    onClose,
    updateRefeshStatus,
    selectedMentionGroup,
  } = props;
  const [isMentionGroupCreating, setIsMentionGroupCreating] = useState(false);
  const [createMentionGroupAPIError, setCreateMentionGroupAPIError] =
    useState("");
  const [adminList, setAdminList] = useState([]);

  const generalParams = useGetGeneralParameters();

  const getFormAdmins = (admins) => {
    const adminArray = [];
    if (admins !== undefined && admins !== null && admins.length) {
      admins.map((admin) => {
        adminArray.push({
          value: admin.id,
          label: admin.name,
        });
      });
    }
    return adminArray;
  };

  const [formData, setFormData] = useState({
    name: {
      required: true,
      value: selectedMentionGroup?.name ?? "",
      error: "",
    },
    description: {
      required: true,
      value: selectedMentionGroup?.description ?? "",
      error: "",
    },
    admins: {
      required: true,
      value: getFormAdmins(selectedMentionGroup?.admins) ?? [],
      error: "",
    },
    country: {
      required: true,
      value: selectedMentionGroup?.country?.id ?? "",
      error: "",
    },
    isActive: {
      required: true,
      value: selectedMentionGroup?.isActive ?? true,
      error: "",
    },
  });

  const onCountryChange = (e) => {
    setFormData({
      ...formData,
      country: {
        value: e.value,
        error: "",
        required: true,
      },
      admins: {
        value: [],
        error: "",
        required: true,
      },
    });
  };
  const onDescriptionChange = (e) => {
    setFormData({
      ...formData,
      description: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };
  const onNameChange = (e) => {
    setFormData({
      ...formData,
      name: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onAdminsChange = (filter) => {
    setFormData({
      ...formData,
      admins: {
        value: filter,
        error: "",
        required: true,
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.country.value) {
      isFormValid = false;
      data.country.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.name.value) {
      isFormValid = false;
      data.name.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (data.admins.value?.length === 0) {
      isFormValid = false;
      data.admins.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    const adminArray = [];
    if (formData.admins.value.length) {
      formData.admins.value.map((admin) => {
        adminArray.push({
          id: admin.value,
          name: admin.label,
        });
      });
    }
    const data = {
      id: isEditMode ? selectedMentionGroup.id : "",
      name: formData.name.value,
      description: formData.description.value,
      country: JSON.stringify({
        id: formData.country.value,
      }),
      admins: JSON.stringify(adminArray),
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsMentionGroupCreating(true);
      setCreateMentionGroupAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post("/mention/group", getFormData())
          .then(() => {
            setIsMentionGroupCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsMentionGroupCreating(false);
            setCreateMentionGroupAPIError(error.response?.data?.message ?? "");
          });
      }
      if (id && isEditMode) {
        axios
          .put(`/mention/group/${id}`, getFormData())
          .then(() => {
            setIsMentionGroupCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsMentionGroupCreating(false);
            setCreateMentionGroupAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  const fetchAdmins = () => {
    axios
      .get(
        `/general/search?search=&limit=-1&orderBy=asc&orderColumn=name&countries=[${formData.country.value}]&objectTypes=["admin"]`
      )
      .then((response) => {
        const adminData = response.data.admins.map((admin) => {
          return {
            label: admin.name,
            value: String(admin.id),
          };
        });
        setAdminList(adminData);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    if (formData.country.value !== "") fetchAdmins();
  }, [formData.country.value]);

  return (
    <Dialog width="50rem">
      <div>
        <HeaderWrapper>
          <Title>
            {title} {id ? "#" + id : ""}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close mention group popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      id="label"
                      type="text"
                      label={t("MENTION_GROUP_NAME")}
                      isFloat
                      required
                      value={String(formData.name.value)}
                      onChange={(e) => onNameChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.name.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.name.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.name.error}
                      placeholder={t("MENTION_GROUP_NAME")}
                    />
                    <Dropdown
                      id="mention-group-country"
                      label={t("COUNTRY")}
                      name="country"
                      value={formData.country.value}
                      onChange={onCountryChange}
                      defaultOption={t("COUNTRY")}
                      options={generalParams.countryList}
                      labelStyles={
                        formData.country.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.country.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.country.error}
                    />
                  </GridRow>
                  <GridRow column="1fr">
                    <TextInput
                      id="payterm-term_calculation_value"
                      type="text"
                      label={t("MENTION_GROUP_DESCRIPTION")}
                      isFloat
                      value={String(formData.description.value)}
                      onChange={(e) => onDescriptionChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.description.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.description.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.description.error}
                      placeholder={t("MENTION_GROUP_DESCRIPTION")}
                    />
                  </GridRow>
                  <GridRow>
                    <div className="add-enquiry__dropdown">
                      <DropdownWithSearch
                        placeholder={t("MENTION_GROUP_ADMINS")}
                        label={t("MENTION_GROUP_ADMINS")}
                        value={
                          formData.admins?.value.length > 0
                            ? formData.admins?.value
                            : []
                        }
                        options={adminList}
                        required
                        isMulti
                        error={formData.admins.error}
                        onChange={onAdminsChange}
                        labelStyles={
                          formData.admins.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputClass={
                          formData.admins.error !== ""
                            ? "dropdown-with-search__dropdown-input-error"
                            : "dropdown-with-search__dropdown-input"
                        }
                      />
                    </div>
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createMentionGroupAPIError && (
            <div>{createMentionGroupAPIError}</div>
          )}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isMentionGroupCreating && <ClipLoader size={15} color="#fff" />}
              {!isMentionGroupCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditMentionGroup.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditMentionGroup.defaultProps = {
  id: null,
  isEditMode: false,
};
export default withTranslation()(AddEditMentionGroup);
